import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { button, input, primary, Notice } from './ui';
import * as api from './finance.service';
import { financeError, financeStatuses, money, parseMoneyMinor, safeReceiptURL } from './finance-domain';
import { formatAgencyDateTime as formatDateTime } from './workflow.service';

export function useFinanceResource<T>(load: () => Promise<T>, deps: unknown[]) {
  const [data, setData] = useState<T>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let active = true;
    setLoading(true); setData(undefined); setError('');
    load().then(value => { if (active) setData(value); })
      .catch(e => { if (active) setError(financeError(e)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [...deps, tick]);
  return { data, loading, error, reload: () => setTick(value => value + 1) };
}

export function FinanceLoadState({ loading, error, empty, reload }: { loading: boolean; error: string; empty: boolean; reload: () => void }) {
  if (loading) return <p role="status" className="py-8 text-center text-sm text-gray-500">正在加载资金数据…</p>;
  if (error) return <div className="space-y-3"><Notice error={error} /><button className={button} onClick={reload}>重试</button></div>;
  if (empty) return <p className="py-8 text-center text-sm text-gray-500">暂无符合条件的记录</p>;
  return null;
}

export function FinanceModal({ title, busy, close, children }: { title: string; busy?: boolean; close: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => { const dialog = ref.current; dialog?.showModal(); return () => { if (dialog?.open) dialog.close(); }; }, []);
  return <dialog ref={ref} aria-label={title} onCancel={e => { e.preventDefault(); if (!busy) close(); }}
    className="fixed inset-0 m-auto max-h-[90dvh] w-[calc(100%_-_2rem)] max-w-xl overflow-y-auto rounded-xl border border-gray-200 bg-white p-0 text-gray-900 shadow-xl backdrop:bg-black/40">
    <div className="sticky top-0 z-10 flex items-center justify-between gap-4 border-b border-gray-200 bg-white px-5 py-4">
      <h2 className="text-base font-semibold">{title}</h2>
      <button type="button" disabled={busy} aria-label="关闭弹窗" className={`${button} shrink-0`} onClick={close}>关闭</button>
    </div>
    <div className="space-y-4 p-5">{children}</div>
  </dialog>;
}

export function BalanceSummary({ account }: { account: api.FinanceAccount }) {
  return <dl className="grid grid-cols-1 gap-3 sm:grid-cols-3">
    {[['可用余额', account.available_minor], ['冻结余额', account.frozen_minor], ['账户总额', account.total_minor]].map(([title, value]) =>
      <div key={title} className="min-w-0 rounded-lg bg-gray-50 p-3"><dt className="text-xs text-gray-500">{title}</dt><dd className="mt-1 break-all text-lg font-semibold tabular-nums">{money(value as number)}</dd></div>)}
  </dl>;
}

export function BalanceDialog({ id, kind, close, done }: { id: number; kind: 'recharge' | 'deduct'; close: () => void; done: (message: string) => void }) {
  const account = useFinanceResource(() => api.getFinanceAccount(id), [id]);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [reference, setReference] = useState('');
  const [receipt, setReceipt] = useState('');
  const [taskID, setTaskID] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const inFlight = useRef(false);
  const keys = useRef(new Map<string, string>());
  const recharge = kind === 'recharge';
  const minor = parseMoneyMinor(amount);
  const receiptURL = safeReceiptURL(receipt);
  const taskValid = !taskID || (/^[1-9]\d*$/.test(taskID) && Number.isSafeInteger(Number(taskID)));
  const excess = !recharge && minor !== null && account.data && minor > account.data.available_minor;
  const beyondLimit = recharge && minor !== null && account.data && !Number.isSafeInteger(account.data.total_minor + minor);
  const valid = !!account.data && minor !== null && !!reason.trim() && !excess && !beyondLimit && taskValid && (recharge ? receiptURL !== null : true);
  async function submit() {
    if (!valid || !minor || inFlight.current) return;
    inFlight.current = true; setBusy(true); setError('');
    const values = { amount_minor: minor, reason: reason.trim(), reference: reference.trim(), receipt_url: recharge ? receiptURL || undefined : undefined, portal_task_id: taskID ? Number(taskID) : undefined };
    const fingerprint = JSON.stringify(values);
    const key = keys.current.get(fingerprint) ?? crypto.randomUUID();
    keys.current.set(fingerprint, key);
    try {
      const result = await api.changeFinanceBalance(id, kind, { ...values, idempotency_key: key });
      done(`${recharge ? '充值' : '平账扣款'}成功，流水 #${result.entry.id}，当前可用余额 ${money(result.account.available_minor)}`);
      close();
    } catch (e) { setError(financeError(e)); }
    finally { inFlight.current = false; setBusy(false); }
  }
  return <FinanceModal title={recharge ? '商户线下充值' : '商户平账扣款'} busy={busy} close={close}>
    <FinanceLoadState {...account} empty={false} />
    {account.data && <>
      <p className="break-words font-medium">{account.data.agent_name} <span className="text-sm text-gray-400">#{id}</span></p>
      <BalanceSummary account={account.data} />
      <p className="text-sm leading-6 text-gray-500">{recharge ? '确认已收到商户线下付款后充值，提交后立即增加可用余额。' : '用于补扣漏计或少计费用，仅扣除可用余额，并保留独立流水。'}</p>
      <form className="space-y-4" onSubmit={e => { e.preventDefault(); void submit(); }}>
        <fieldset disabled={busy} className="min-w-0 space-y-4 disabled:opacity-60">
          <label className="block text-sm">{recharge ? '充值金额（元）' : '扣款金额（元）'}<input className={`${input} mt-1 w-full`} inputMode="decimal" autoComplete="off" value={amount} onChange={e => setAmount(e.target.value)} placeholder="请输入大于 0 的金额，最多 2 位小数" /></label>
          {amount && minor === null && <p role="alert" className="text-sm text-red-600">金额须大于 0，最多保留 2 位小数。</p>}
          {excess && <p role="alert" className="text-sm text-red-600">扣款金额超过可用余额 {money(account.data.available_minor)}，不能扣除冻结资金。</p>}
          {beyondLimit && <p role="alert" className="text-sm text-red-600">充值后账户总额超过支持的金额上限。</p>}
          <label className="block text-sm">{recharge ? '充值说明' : '平账原因'}<textarea className="mt-1 min-h-20 w-full rounded-md border border-gray-300 p-3 text-sm" maxLength={500} required value={reason} onChange={e => setReason(e.target.value)} placeholder={recharge ? '如：10 月 30 日银行转账充值' : '请说明漏计或少计费用的原因'} /></label>
          <label className="block text-sm">{recharge ? '转账流水号 / 备注（可选）' : '关联账单 / 备注（可选）'}<input className={`${input} mt-1 w-full`} maxLength={200} value={reference} onChange={e => setReference(e.target.value)} /></label>
          <label className="block text-sm">关联商户任务编号（可选）<input className={`${input} mt-1 w-full`} inputMode="numeric" value={taskID} onChange={e => setTaskID(e.target.value)} placeholder="填写该商户的任务编号" /></label>
          {!taskValid && <p role="alert" className="text-sm text-red-600">任务编号须为正整数。</p>}
          {recharge && <label className="block text-sm">收款凭证链接（可选）<input className={`${input} mt-1 w-full`} maxLength={2000} value={receipt} onChange={e => setReceipt(e.target.value)} placeholder="https://…，未填写也可充值" /></label>}
          {recharge && receiptURL === null && <p role="alert" className="text-sm text-red-600">凭证链接须为完整 HTTP 或 HTTPS 地址。</p>}
        </fieldset>
        <Notice error={error} />
        {minor !== null && <div className="rounded-lg bg-gray-50 p-3 text-sm">本次{recharge ? '增加' : '扣除'}可用余额 <strong>{money(minor)}</strong>{!excess && <span>，操作后为 <strong>{money(account.data.available_minor + (recharge ? minor : -minor))}</strong>。</span>}</div>}
        <div className="flex flex-wrap justify-end gap-3"><button type="button" className={button} disabled={busy} onClick={account.reload}>刷新余额</button><button type="button" className={button} disabled={busy} onClick={close}>取消</button><button className={recharge ? primary : `${button} border-red-600 bg-red-600 text-white hover:bg-red-700`} disabled={!valid || busy}>{busy ? '处理中…' : recharge ? '确认充值' : '确认扣款'}</button></div>
      </form>
    </>}
  </FinanceModal>;
}

export function SettlementDialog({ id, canSettle, close, done }: { id: number; canSettle: boolean; close: () => void; done: (message: string) => void }) {
  const resource = useFinanceResource(() => api.getFinanceTask(id), [id]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const key = useRef(crypto.randomUUID());
  const task = resource.data;
  async function settle() {
    if (!task?.can_settle || !canSettle || inFlight.current) return;
    inFlight.current = true; setBusy(true); setError('');
    try { await api.settleFinanceTask(task, key.current); done(`商户任务 #${id} 结算成功，扣款 ${money(task.charge_minor)}，解冻 ${money(task.release_minor)}`); close(); }
    catch (e) { setError(financeError(e)); }
    finally { inFlight.current = false; setBusy(false); }
  }
  return <FinanceModal title="商户任务结算详情" busy={busy} close={close}>
    <FinanceLoadState {...resource} empty={false} />
    {task && <>
      <p className="break-words font-medium">{task.title}</p><p className="break-words text-sm text-gray-500">{task.agent_name} · 商户任务 #{task.portal_task_id}</p>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
        {[
          ['资金状态', financeStatuses[task.status] ?? task.status], ['任务名额', task.quota],
          ['每份任务金额', money(task.unit_amount_minor)], ['提交时冻结预算', money(task.budget_minor)],
          ['当前冻结金额', money(task.frozen_minor)], ['有效完成数量', task.valid_count],
          ['领取未完成数量', task.in_flight_count ?? 0], ['待审核数量', task.pending_review_count ?? 0],
          [task.status === 'settled' ? '实际扣款' : '预计扣款', money(task.charge_minor)],
          [task.status === 'settled' ? '已解冻余额' : '预计解冻余额', money(task.release_minor)],
        ].map(([label, value]) => <div key={label} className="min-w-0"><dt className="text-gray-500">{label}</dt><dd className="mt-1 break-all font-medium tabular-nums">{value}</dd></div>)}
      </dl>
      {task.status === 'settled' ? <p className="rounded-md bg-green-50 p-3 text-sm text-green-800">{task.settled_at ? `已于 ${formatDateTime(task.settled_at)}（北京时间）` : '已'}{task.settlement_mode === 'auto' ? '自动' : '人工'}结算，资金流水已记录。</p> : <p className={`rounded-md p-3 text-sm leading-6 ${task.can_settle ? 'bg-blue-50 text-blue-800' : 'bg-amber-50 text-amber-800'}`}>{task.can_settle ? '结算后按有效完成数量扣除冻结资金，其余金额返回可用余额。此操作不会再次发放会员奖励。' : task.block_reason || '当前任务不满足结算条件。'}</p>}
      <Notice error={error} />
      <div className="flex flex-wrap justify-end gap-3"><button className={button} disabled={busy} onClick={() => { setError(''); resource.reload(); }}>刷新结算预览</button><button className={button} disabled={busy} onClick={close}>关闭详情</button>{canSettle && task.status === 'frozen' && <button className={primary} disabled={busy || !task.can_settle} onClick={settle}>{busy ? '结算中…' : '确认结算'}</button>}</div>
    </>}
  </FinanceModal>;
}

export function FinanceAccountLink({ id, children }: { id: number; children: ReactNode }) {
  return <Link className="text-primary hover:underline" to={`/agent-finance?tab=ledger&agent_id=${id}`}>{children}</Link>;
}
